import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { defaultDateRangeForTz } from "@/lib/timezone";

/** Shared date + optional campaign/agent filters for analytics reports. */
export default function ReportFilters({
  dateFrom,
  dateTo,
  onDateFrom,
  onDateTo,
  campaignId,
  onCampaignId,
  campaigns = [],
  agentId,
  onAgentId,
  agents = [],
  onApply,
  onClear,
  applying = false,
  testid = "report-filters",
  timezone,
}) {
  return (
    <form
      className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end"
      onSubmit={(e) => { e.preventDefault(); onApply?.(); }}
      data-testid={testid}
    >
      <div className="space-y-1.5">
        <Label>From{timezone ? ` (${timezone})` : ""}</Label>
        <Input type="date" value={dateFrom || ""} onChange={(e) => onDateFrom?.(e.target.value)} data-testid={`${testid}-from`} />
      </div>
      <div className="space-y-1.5">
        <Label>To</Label>
        <Input type="date" value={dateTo || ""} onChange={(e) => onDateTo?.(e.target.value)} data-testid={`${testid}-to`} />
      </div>
      {onCampaignId ? (
        <div className="space-y-1.5">
          <Label>Campaign</Label>
          <Select value={campaignId || "all"} onValueChange={(v) => onCampaignId(v === "all" ? "" : v)}>
            <SelectTrigger data-testid={`${testid}-campaign`}><SelectValue placeholder="All campaigns" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All campaigns</SelectItem>
              {campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name || c.id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
      {onAgentId ? (
        <div className="space-y-1.5">
          <Label>Agent</Label>
          <Select value={agentId || "all"} onValueChange={(v) => onAgentId(v === "all" ? "" : v)}>
            <SelectTrigger data-testid={`${testid}-agent`}><SelectValue placeholder="All agents" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All agents</SelectItem>
              {agents.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name || a.id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" className="rounded-sm" disabled={applying} data-testid={`${testid}-apply`}>
          {applying ? "Loading…" : "Apply"}
        </Button>
        <Button type="button" variant="outline" className="rounded-sm" onClick={onClear} data-testid={`${testid}-clear`}>
          Clear
        </Button>
      </div>
    </form>
  );
}

export const REPORT_PAGE_SIZE = 500;

/** Default report range — today only unless days overridden. */
export function defaultDateRange(days = 1, timeZone = "UTC") {
  return defaultDateRangeForTz(timeZone, days);
}

export function ReportPagination({
  page = 1,
  pageSize = REPORT_PAGE_SIZE,
  total = 0,
  onPage,
  testid = "report-pagination",
}) {
  const pages = Math.max(1, Math.ceil(Number(total || 0) / pageSize));
  if (Number(total || 0) <= pageSize) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-1 py-3" data-testid={testid}>
      <span className="text-sm text-muted-foreground">
        Page {page} of {pages} · {total} records
      </span>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-sm"
          disabled={page <= 1}
          onClick={() => onPage?.(page - 1)}
          data-testid={`${testid}-prev`}
        >
          Prev
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-sm"
          disabled={page >= pages}
          onClick={() => onPage?.(page + 1)}
          data-testid={`${testid}-next`}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

export function formatSec(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  return `${m}:${String(r).padStart(2, "0")}`;
}
